// src/modules/meal/services/mealService.js

import authService from '../../auth/services/authService';
import { buildApiUrl } from '../../../config/api';

// Helper para manejar respuestas y redirigir en caso de 401
const handleResponse = async (response) => {
    if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Token expirado');
    }
    return response;
};

export const mealService = {
    /**
     * Obtener comida actual
     */
    async getCurrentMeal() {
        const response = await fetch(buildApiUrl('/api/user/now'), {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al obtener la comida actual');
        }

        return await response.json();
    },

    /**
     * Obtener todas las comidas con paginación y filtros
     */
    async getAllMeals(page = 1, limit = 20, searchTerm = '', filters = {}) {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString()
        });

        if (searchTerm) {
            params.append('search', searchTerm);
        }

        Object.keys(filters).forEach(key => {
            if (filters[key] !== undefined && filters[key] !== '') {
                params.append(key, filters[key].toString());
            }
        });

        const response = await fetch(buildApiUrl(`/api/user/meals?${params}`), {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al obtener las comidas');
        }

        return await response.json();
    },

    /**
     * Buscar comidas
     */
    async searchMeals(searchTerm, filters = {}) {
        const params = new URLSearchParams({
            search: searchTerm
        });

        Object.keys(filters).forEach(key => {
            if (filters[key] !== undefined && filters[key] !== '') {
                params.append(key, filters[key].toString());
            }
        });

        const response = await fetch(buildApiUrl(`/api/user/meals/search?${params}`), {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al buscar comidas');
        }

        return await response.json();
    },

    /**
     * Archivar comida
     */
    async archiveMeal(mealId) {
        const response = await fetch(buildApiUrl(`/api/user/meal/${mealId}/archive`), {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al archivar la comida');
        }

        return await response.json();
    },

    /**
     * Marcar/desmarcar comida como favorita
     */
    async toggleMealFavorite(mealId) {
        const response = await fetch(buildApiUrl(`/api/user/meal/${mealId}/favorite`), {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al cambiar el estado de favorito');
        }

        return await response.json();
    },

    /**
     * Obtener receta por ID con pasos de cocina
     */
    async getMealByIdWithSteps(mealId) {
        const response = await fetch(buildApiUrl(`/api/user/recipes/${mealId}`), {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al obtener los detalles de la receta');
        }

        return await response.json();
    },

    /**
     * Marcar/desmarcar comida como repetible
     */
    async toggleMealRepeatable(mealId) {
        const response = await fetch(buildApiUrl(`/api/user/meal/${mealId}/toggle-repeatable`), {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al cambiar el estado de repetible');
        }

        return await response.json();
    },

    /**
     * Asignar una receta de biblioteca a un slot del plan
     */
    async assignRecipeToSlot(targetMealId, recipeId) {
        const response = await fetch(buildApiUrl(`/api/user/meals/${targetMealId}/assign-recipe`), {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ recipeId }),
        });

        await handleResponse(response);

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Error al asignar la receta');
        }

        return await response.json();
    }
};
